import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { IC_ERROR_GRAPHIC, IC_PAGE_NOT_FOUND } from '../../Assets/images';
import { useErrorHandling } from '../ErrorBoundary/ErrorBoundaryContext';
import { ErrorPageStyled } from './ErrorPageStyled';
import { useDispatch, useSelector } from 'react-redux';
import * as apiActions from 'aster-foundation/core/astercore/auth/login/redux/actions';
import Header from '../Header/Header';
import HeaderBelow from '../HeaderBelow/HeaderBelow';
import TopBar from '../TopBar/TopBar';
import HelpPopUp from '../HelpPopUp/HelpPopUp';
import { dataLayerSendEvent } from '@common/Utility/analytics-utils';
import { errorHandling, getReferralPageForGA } from '@common/Utility/common-utils';
import ContactFormSidebar from '../Footer/ContactFormSidebar';
import { useAuth } from '@common/AuthService';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';

const ErrorPage = (props) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const generateURL = useNavigationURL();
  const { resetError } = useErrorHandling();
  const [showHelp, setShowHelp] = useState(false);
  const { authCtx, initialized } = useAuth();
  const userLogin = useSelector((state) => state?.astercore?.checkUserExist?.data?.entry[0]?.resource);
  const [showContactSideBar, setShowContactSideBar] = useState(false);

  useEffect(() => {
    if (!userLogin) {
      if (authCtx?.authenticated && initialized) {
        dispatch(
          apiActions.checkUserExist({
            params: {
              mobile: encodeURIComponent(authCtx?.idTokenParsed?.mobile_number),
              id: authCtx?.idTokenParsed?.sub,
            },
            onSuccess: () => {},
            onFailure: (err) => {
              const pageType = getReferralPageForGA(router?.asPath);
              const screenName = router?.pathname;
              errorHandling(err, true, screenName, pageType);
            },
          }),
        );
      }
    }
  }, [userLogin, authCtx?.authenticated, initialized]);

  useEffect(() => {
    if (props.page === 'something_went_wrong') {
      dataLayerSendEvent('http_error', {
        error: props.page,
        referrer_page: props?.history,
      });
    } else {
      dataLayerSendEvent('http_error', {
        error: 'Error 404 - Page Not Found',
        referrer_page: props?.history ? props?.history : getReferralPageForGA(router?.asPath?.split('/')),
      });
    }
  }, []);

  return (
    <ErrorPageStyled>
      {props.page !== 'something_went_wrong' && (
        <>
          <TopBar screenName="Error page" pageType="Error" />
          <Header />
        </>
      )}
      <div className={props.page === 'something_went_wrong' ? 'errorContent went_wrong' : 'errorContent error_page'}>
        {props.page !== 'something_went_wrong' && <HeaderBelow />}
        <div className="text-center px-3">
          {props.page === 'something_went_wrong' ? (
            <Image src={IC_ERROR_GRAPHIC.src} alt="Error Page" className="mx-auto img-fluid" width={550} height={550} />
          ) : (
            <Image
              src={IC_PAGE_NOT_FOUND.src}
              alt="Error Page"
              className="mx-auto mt-5 img-fluid"
              width={210}
              height={271}
            />
          )}
          {props.page === 'something_went_wrong' ? (
            <>
              <div className="font_20">Something went wrong here, please try again. If the issue continues please</div>
              <div
                className="contact-link cursor-pointer"
                onClick={() => {
                  setShowHelp(true);
                }}
              >
                <a>Contact us.</a>
              </div>
              <div className="mt-5">
                <button
                  data-test="try_Again"
                  className="btn btn-primary tryAgainBtn "
                  type="button"
                  onClick={() => {
                    router?.reload(window.location.pathname);
                  }}
                >
                  Try Again
                </button>
                <button
                  data-test="Go_to_Home"
                  className="btn btn-outline-primary goToHomeBtn "
                  type="button"
                  onClick={() => {
                    resetError && resetError();
                    router.push({ pathname: generateURL(`/`) });
                  }}
                >
                  Go to Home
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="font_20">
                Unfortunately we can&apos;t seem to find the page you are looking for.
                <br /> Please&nbsp;
                <span
                  className="contact-link cursor-pointer"
                  onClick={() => {
                    setShowHelp(true);
                  }}
                >
                  <a>Contact us</a>
                </span>
                &nbsp;for any inquiries.
              </div>
              <div className="mt-5">
                <button
                  data-test="Back_to_Home"
                  className="btn btn-primary goToHomeBtn"
                  type="button"
                  onClick={() => {
                    router.push({ pathname: generateURL(`/`) });
                  }}
                >
                  Back to Home
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {showHelp && (
        <HelpPopUp
          close={() => {
            setShowHelp(false);
          }}
          setShowContactSideBar={setShowContactSideBar}
        />
      )}
      {showContactSideBar && (
        <ContactFormSidebar
          wrapperClassName="contact cursor-pointer"
          showLabel={false}
          setShowContactSideBar={setShowContactSideBar}
        />
      )}
    </ErrorPageStyled>
  );
};

ErrorPage.propTypes = {
  page: PropTypes.any,
  history: PropTypes.string,
};

export default ErrorPage;
